import React from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { t } from '../../../domain/services/configService';
import { theme } from '../../../domain/theme';
import { SmartLink } from '../../SmartLink';
import { Image } from '../../Image';
import { PlayButton } from '../atoms/PlayButton';
import { imageResizerService } from '../../../domain/services/imageResizerService';
import { IconArticle } from '../../Icon/IconArticle';
import { IconGallery } from '../../Icon/IconGallery';
import { AnimatedImage } from '../../Image/AnimatedImage';
import { isString } from '../../../domain/utils/typeUtils';

const baseStyles = { aspect: '16x9', videoIconSize: 's' };

export const CardImageLegacy = ({
  type,
  url,
  target,
  title,
  imageEntity,
  bgImageEntity,
  imageMaxWidths,
  styles,
  isAboveTheFold,
  hover,
  icon,
  animatedImageEntities,
  dataLayerClickEvent
}) => {
  const cardImageStyleAttributes = { ...baseStyles, ...styles };
  const baseSpace = theme.spacing('base');
  const bgImageRatio = {
    mobile: 0.5625,
    tablet: null,
    small_desktop: null,
    large_desktop: null
  };
  if (bgImageEntity) {
    if (isString(cardImageStyleAttributes.aspect)) {
      const aspectRatio = imageResizerService.getAspectRatioFromString(
        cardImageStyleAttributes.aspect
      );
      bgImageRatio.mobile = aspectRatio.height / aspectRatio.width;
    } else {
      if (cardImageStyleAttributes.aspect.mobile) {
        const aspectRatio = imageResizerService.getAspectRatioFromString(
          cardImageStyleAttributes.aspect.mobile
        );
        bgImageRatio.mobile = aspectRatio.height / aspectRatio.width;
      }
      if (cardImageStyleAttributes.aspect.mobile) {
        const aspectRatio = imageResizerService.getAspectRatioFromString(
          cardImageStyleAttributes.aspect.mobile
        );
        bgImageRatio.mobile = aspectRatio.height / aspectRatio.width;
      }
      if (cardImageStyleAttributes.aspect.tablet) {
        const aspectRatio = imageResizerService.getAspectRatioFromString(
          cardImageStyleAttributes.aspect.tablet
        );
        bgImageRatio.tablet = aspectRatio.height / aspectRatio.width;
      }
      if (cardImageStyleAttributes.aspect.small_desktop) {
        const aspectRatio = imageResizerService.getAspectRatioFromString(
          cardImageStyleAttributes.aspect.small_desktop
        );
        bgImageRatio.small_desktop = aspectRatio.height / aspectRatio.width;
      }
      if (cardImageStyleAttributes.aspect.large_desktop) {
        const aspectRatio = imageResizerService.getAspectRatioFromString(
          cardImageStyleAttributes.aspect.large_desktop
        );
        bgImageRatio.large_desktop = aspectRatio.height / aspectRatio.width;
      }
    }
  }

  const imageStyles =
    bgImageEntity &&
    css`
      width: calc(100% - ${baseSpace});
      padding: ${baseSpace} 0 calc(${bgImageRatio.mobile * 100}% - ${baseSpace})
        0;
      ${bgImageRatio.tablet
        ? `${theme.mq.tablet} {padding: ${baseSpace} 0 calc(${
            bgImageRatio.tablet * 100
          }% - ${baseSpace})} 0`
        : ''}
      ${bgImageRatio.small_desktop
        ? `${theme.mq.small_desktop} {padding: ${baseSpace} 0 calc(${
            bgImageRatio.small_desktop * 100
          }% - ${baseSpace})} 0`
        : ''}
          ${bgImageRatio.large_desktop
        ? `${theme.mq.large_desktop} {padding: ${baseSpace} 0 calc(${
            bgImageRatio.large_desktop * 100
          }% - ${baseSpace})} 0`
        : ''}
      height: 0;
      position: relative;
      overflow: hidden;
      z-index: 2;
    `;
  const iconImageStyle =
    icon &&
    css`
      position: relative;
      svg {
        position: absolute;
        bottom: 0;
        margin: 10px;
      }
    `;

  const bgImageStyles =
    bgImageEntity &&
    css`
      width: calc(100% - ${baseSpace});
      padding-bottom: calc(${bgImageRatio.mobile * 100}% - ${baseSpace});
      height: 0;
      position: absolute;
      overflow: hidden;
      left: ${baseSpace};
      top: 0;
      background: #000;
      z-index: 1;
      picture {
        opacity: 0.6;
      }
    `;

  const bgImageCtaStyles =
    bgImageEntity &&
    css`
      ${theme.font.family('boldWeb')};
      position: absolute;
      top: 0;
      left: ${baseSpace};
      font-size: 0.75rem;
      letter-spacing: 3px;
      color: #fff;
      line-height: 2.25rem;
      text-transform: uppercase;
    `;

  const showIconGallery = type === 'photo_gallery' && !bgImageEntity;
  const iconGalleryStyles = showIconGallery
    ? css`
        position: absolute;
        bottom: 0;
        left: 0;
        padding: ${theme.spacing('xs')};
        background-color: ${hover ? '#fc0' : 'rgba(0, 0, 0, 0.8)'};
      `
    : null;

  let IconComponent;
  if (icon) {
    switch (icon) {
      case 'article':
        IconComponent = IconArticle;
        break;
      case 'gallery':
        IconComponent = IconGallery;
        break;
      default:
        throw new Error(`Icon type ${icon} not found.`);
    }
  }

  return (
    <SmartLink
      target={target}
      to={url}
      title={title}
      dataLayerClickEvent={dataLayerClickEvent}
    >
      <div css={[imageStyles, iconImageStyle]}>
        {animatedImageEntities.length > 1 ? (
          <AnimatedImage
            entities={animatedImageEntities}
            aspect={cardImageStyleAttributes.aspect}
            maxWidths={imageMaxWidths}
            loading={isAboveTheFold ? 'eager' : 'lazy'}
          />
        ) : (
          <Image
            entity={imageEntity}
            aspect={cardImageStyleAttributes.aspect}
            maxWidths={imageMaxWidths}
            loading={isAboveTheFold ? 'eager' : 'lazy'}
          />
        )}

        {IconComponent && <IconComponent color="white" size={20} />}
      </div>
      {bgImageEntity && (
        <div css={bgImageStyles}>
          <Image
            entity={bgImageEntity}
            aspect={cardImageStyleAttributes.aspect}
            maxWidths={imageMaxWidths}
            loading={isAboveTheFold ? 'eager' : 'lazy'}
          />
          <div css={bgImageCtaStyles}>{t('Photo Gallery')}</div>
        </div>
      )}
      {type === 'video' && (
        <PlayButton
          hover={hover}
          size={cardImageStyleAttributes.videoIconSize}
        />
      )}
      {showIconGallery && (
        <div css={iconGalleryStyles} data-icon-type="card-image-gallery">
          <IconGallery
            color={hover ? '#000' : '#fc0'}
            size={24}
            display="block"
          />
        </div>
      )}
    </SmartLink>
  );
};

CardImageLegacy.defaultProps = {
  bgImageEntity: undefined,
  imageMaxWidths: undefined,
  styles: { aspect: '16x9', videoIconSize: 's' },
  isAboveTheFold: false,
  hover: false,
  icon: undefined,
  animatedImageEntities: [],
  dataLayerClickEvent: undefined,
  target: undefined
};

CardImageLegacy.propTypes = {
  type: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  target: PropTypes.string,
  title: PropTypes.string.isRequired,
  imageEntity: PropTypes.objectOf(PropTypes.any).isRequired,
  bgImageEntity: PropTypes.objectOf(PropTypes.any),
  imageMaxWidths: PropTypes.objectOf(PropTypes.any),
  styles: PropTypes.shape({
    aspect: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.objectOf(PropTypes.string)
    ]),
    videoIconSize: PropTypes.string
  }),
  isAboveTheFold: PropTypes.bool,
  hover: PropTypes.bool,
  icon: PropTypes.string,
  animatedImageEntities: PropTypes.arrayOf(PropTypes.any),
  dataLayerClickEvent: PropTypes.objectOf(PropTypes.any)
};
